"use client";

import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import BarcodeScannerModal from './BarcodeScannerModal';
import { searchProducts } from '@/lib/supabase/products';

type Suggestion = {
  id?: string;
  title?: string;
  brand?: string;
  product_type?: string;
  barcode?: string;
  main_image_url?: string | null;
};

export default function ProductTypeahead({
  value,
  onChange,
  onSelect,
  onCreateNew,
  onOpenBarcodeScanner,
  isLockedFromBarcode = false,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (s: Suggestion) => void;
  onCreateNew: (q: string) => void;
  onOpenBarcodeScanner: () => void;
  isLockedFromBarcode?: boolean;
}) {
  const [showScanner, setShowScanner] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => setQuery(value || ''), [value]);

  useEffect(() => {
    const id = setTimeout(async () => {
      const q = query.trim();
      if (q.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const r = await searchProducts(q, 12);
        setResults(r || []);
      } catch (e) {
        console.warn('ProductTypeahead search error', e);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(id);
  }, [query]);

  const onChoose = (s: Suggestion) => {
    onSelect(s);
    setQuery(s.title || '');
    onChange(s.title || '');
  };

  const showCreate = !loading && query.trim().length > 0 && !results.some(r => (r.title||'').toLowerCase() === query.trim().toLowerCase());

  return (
    <>
    <div className="relative">
      <div className="flex items-center gap-2">
        <input
          value={query}
          onChange={(e) => { if (!isLockedFromBarcode) { setQuery(e.target.value); onChange(e.target.value); } }}
          placeholder="Buscá un producto o escribí uno nuevo"
          disabled={isLockedFromBarcode}
          className={`w-full px-3 py-2 border rounded ${isLockedFromBarcode ? 'bg-muted text-muted-foreground cursor-not-allowed' : ''}`}
          title={isLockedFromBarcode ? 'Producto auto-completado desde código de barras' : ''}
        />
        <button type="button" onClick={() => { console.debug('[ProductTypeahead] open scanner click'); if (onOpenBarcodeScanner) onOpenBarcodeScanner(); }} className="p-2 rounded border bg-white">
          <img src="/brand/barcode-minibid.svg" alt="Escanear código" width="18" height="18" />
        </button>
      </div>

      { !isLockedFromBarcode && (loading || results.length > 0 || showCreate) && (
        <div className="absolute z-40 mt-1 left-0 right-0 bg-white border rounded shadow max-h-64 overflow-auto">
          {loading && <div className="p-2 text-sm text-muted-foreground">Buscando...</div>}
          {!loading && results.length === 0 && !showCreate && <div className="p-2 text-sm text-muted-foreground">Escribí al menos 2 caracteres para buscar</div>}
          {!loading && results.map((r) => (
            <button key={r.id || (r.title || Math.random())} onClick={() => onChoose(r)} className="w-full text-left p-2 hover:bg-muted/50">
              <div className="text-sm font-medium">{r.title}</div>
              <div className="text-xs text-muted-foreground">{r.brand || r.product_type || ''}</div>
            </button>
          ))}
          {showCreate && (
            <div className="p-2 border-t">
              <button className="w-full text-left p-2 bg-primary text-white rounded" onClick={() => onCreateNew(query)}>
                Crear nuevo producto "{query}"
              </button>
            </div>
          )}
        </div>
      )}
    </div>
      {/* Scanner modal is managed by parent via `onOpenBarcodeScanner` to avoid duplicate mounts */}
    </>
  );
}
